import {
  createFileRoute,
  notFound,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-dark-indigo/theme.css'; // or your chosen theme
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { PageTagline } from '~/components/ui/layout/page/PageTagline';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import React, { useCallback, useEffect, useState } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { endpointFundAnalyticsGET } from '~/api/def/endpoints/fundAnalyticsEndpoint';
import { endpointFundGET } from '~/api/def/endpoints/fundEndpoint';
import {
  fundAnalyticsDataAtom,
  fundAnalyticsListAtom,
  selectedFundAtom,
  STATIC_WIDGET_IDS,
  WidgetGenerationRequest,
  WidgetId,
  widgetOperationAtom,
} from '~/atoms/fundAnalyticsAtom';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { FundListDashboard } from '~/components/ui/tables/ag/fundListDashboard/FundListDashboard';
import { EMSBlotterWidget } from '~/components/ui/widgets/EMSBlotterWidget';
import { ForwardReturnsWidgetWrapper } from '~/components/ui/widgets/ForwardReturnsWidget/ForwardReturnsWidgetWrapper';
import { HistoricalReturnsWidgetWrapper } from '~/components/ui/widgets/HistoricalReturnsWidget/HistoricalReturnsWidgetWrapper';
import { numberStd } from '~/lib/money';
import AggrGrossIrrLineChart from '~/lib/recharts/aggrGrossIrrLineChart';
import AggrGrossMoicLineChart from '~/lib/recharts/aggrGrossMoicLineChart';
import PCompanyInvestedPieChart from '~/lib/recharts/pCompanyInvestedPieChart';
import PCompanySectorPieChart from '~/lib/recharts/pCompanySectorPieChart';
import '~/styles/component/bq-react-grid-layout.css';
import '~/styles/ems-blotter.css';
import { canAccess } from '~/utils/rbac';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

const GridLayout = WidthProvider(RGL);

// Finance-inspired colors from CSS variables
const CHART_COLORS = [
  'var(--chart-color-profit)', // Green (profit)
  'var(--chart-color-stable)', // Blue (stable)
  'var(--chart-color-caution)', // Amber (caution)
  'var(--chart-color-luxury)', // Purple (luxury)
  'var(--chart-color-tech)', // Cyan (tech)
  'var(--chart-color-energy)', // Orange (energy)
  'var(--chart-color-growth)', // Light Green (growth)
  'var(--chart-color-conservative)', // Indigo (conservative)
  'var(--chart-color-disruptive)', // Pink (disruptive)
  'var(--chart-color-loss)', // Red (loss)
];

export const Route = createFileRoute('/_authLayout/fund/overview')({
  beforeLoad: async () => {
    if (!canAccess('fund')) {
      throw notFound();
    }
  },
  component: () => <FundOverviewRoute />,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      fundId: search.fundId as string | undefined,
    };
  },
});

const FundOverviewRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.FUND_MANAGEMENT);
  if (!allowed) return null;
  return <FundOverviewContainer />;
};

export const FundOverviewContainer = () => {
  const navigate = useNavigate();
  const { fundId } = Route.useSearch();
  const location = useLocation();
  const [totalAum, setTotalAum] = useState<number>(0);
  const [totalAssetsDeployed, setTotalAssetsDeployed] = useState<number>(0);
  const [totalPCompanies, setTotalPCompanies] = useState<number>(0);
  const [activeFunds, setActiveFunds] = useState<number>(0);
  const [writeOff, setWriteOff] = useState<number>(0);
  const [exited, setExited] = useState<number>(0);
  const setFundAnalyticsData = useSetAtom(fundAnalyticsDataAtom);
  const setFundAnalyticsListData = useSetAtom(fundAnalyticsListAtom);
  const selectedFund = useAtomValue(selectedFundAtom);
  const setSelectedFund = useSetAtom(selectedFundAtom);
  const widgetOperation = useAtomValue(widgetOperationAtom);

  console.log('widgetOperation', widgetOperation);
  const setWidgetOperation = useSetAtom(widgetOperationAtom);

  // Initialize state from localStorage or default to true (expanded)
  const [showMetrics, setShowMetrics] = useState<boolean>(() => {
    const savedState = localStorage.getItem('metricsExpanded');
    return savedState === null ? true : savedState === 'true';
  });

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('metricsExpanded', String(showMetrics));
  }, [showMetrics]);

  const {
    data: fundListAnalyticsList,
    isLoading,
    error, // Todo: add error handling for this.
    refetch: refetchFundListAnalytics,
  } = useSuspenseQuery({
    queryKey: ['getFundsList'],
    queryFn: () => endpointFundGET(false, true),
  });

  useEffect(() => {
    if (fundListAnalyticsList && fundListAnalyticsList.data) {
      setTotalAum(
        fundListAnalyticsList.data.reduce(
          (acc, fund) => acc + (fund.currentNav || 0),
          0,
        ),
      );
      setTotalAssetsDeployed(
        fundListAnalyticsList.data.reduce(
          (acc, fund) => acc + (fund.assetDeployed || 0),
          0,
        ),
      );
      setTotalPCompanies(
        fundListAnalyticsList.data.reduce(
          (acc, fund) => acc + (fund.portfolioCompanyCount || 0),
          0,
        ),
      );
      setActiveFunds(
        fundListAnalyticsList.data.filter((fund) => fund.status === 'Active')
          .length,
      );
      setExited(
        fundListAnalyticsList.data.reduce(
          (acc, fund) => acc + (fund.exit || 0),
          0,
        ),
      );
      setWriteOff(
        fundListAnalyticsList.data.reduce(
          (acc, fund) => acc + (fund.writeOff || 0),
          0,
        ),
      );
      setFundAnalyticsListData({
        fundList: Array.isArray(fundListAnalyticsList.data)
          ? fundListAnalyticsList.data
          : [],
        loading: isLoading,
        error,
      });
    }
  }, [error, fundListAnalyticsList, isLoading, setFundAnalyticsListData]);

  const {
    data: fundAnalyticsData,
    isLoading: isFundAnalyticsDataLoading,
    error: fundAnalyticsDataError,
  } = useQuery({
    queryKey: [`fundAnalyticsGet/@{fundId}`, selectedFund?.id],
    queryFn: () =>
      selectedFund?.id
        ? endpointFundAnalyticsGET(selectedFund?.id)
        : endpointFundAnalyticsGET(),
  });

  useEffect(() => {
    setFundAnalyticsData({
      pieChartDate: (fundAnalyticsData?.data.pCompanyPieChartGraph || []).map(
        (entry, index) => ({
          ...entry,
          fill: CHART_COLORS[index % CHART_COLORS.length],
        }),
      ),
      grossIRRData: (fundAnalyticsData?.data.irrLineGraph || []).map(
        (entry) => ({
          ...entry,
          fill: '',
        }),
      ),
      grossMOICData: (fundAnalyticsData?.data.moicLineGraph || []).map(
        (entry) => ({
          ...entry,
          fill: '', // Not used with our new approach
        }),
      ),
      fundNames: fundAnalyticsData?.data.allFundNames || [],
      loading: isFundAnalyticsDataLoading,
      error: fundAnalyticsDataError,
    });
  }, [
    fundAnalyticsData?.data.allFundNames,
    fundAnalyticsData?.data.irrLineGraph,
    fundAnalyticsData?.data.moicLineGraph,
    fundAnalyticsData?.data.pCompanyPieChartGraph,
    fundAnalyticsDataError,
    isFundAnalyticsDataLoading,
    setFundAnalyticsData,
  ]);

  useEffect(() => {
    if (!fundId) setSelectedFund(null);
    if (selectedFund) {
      navigate({
        to: '/fund/overview',
        search: { fundId: selectedFund.id },
        replace: true,
      });
    } else setSelectedFund(null);
  }, [fundId, navigate, selectedFund, setSelectedFund]);

  const [widgets, setWidgets] = useState<React.ReactElement[]>([]);

  useEffect(() => {
    const layout = localStorage.getItem('layout');
    if (layout && JSON.parse(layout).length > 0) {
      JSON.parse(layout).forEach((widget: WidgetGenerationRequest) => {
        if (widget.id.includes('companies-per-sector')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <PCompanySectorPieChart widgetId={widget.id} key={widget.id} />,
          ]);
        } else if (widget.id.includes('invested-per-sector')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <PCompanyInvestedPieChart widgetId={widget.id} key={widget.id} />,
          ]);
        } else if (widget.id.includes('aggr-gross-moic')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <AggrGrossMoicLineChart widgetId={widget.id} key={widget.id} />,
          ]);
        } else if (widget.id.includes('aggr-gross-irr')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <AggrGrossIrrLineChart widgetId={widget.id} key={widget.id} />,
          ]);
        } else if (widget.id.includes('fund-table')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <FundListDashboard
              widgetId={widget.id}
              refetchFundListAnalytics={refetchFundListAnalytics}
            />,
          ]);
        } else if (widget.id.includes('ems-blotter')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <EMSBlotterWidget widgetId={widget.id} key={widget.id} />,
          ]);
        } else if (widget.id.includes('historical-returns')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <HistoricalReturnsWidgetWrapper
              widgetId={widget.id}
              key={widget.id}
              title="Historical Returns"
            />,
          ]);
        } else if (widget.id.includes('forward-returns')) {
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <ForwardReturnsWidgetWrapper
              widgetId={widget.id}
              key={widget.id}
              title="Forward Returns"
            />,
          ]);
        }
      });
    } else {
      setWidgets([
        <FundListDashboard
          widgetId="fund-list-widget"
          refetchFundListAnalytics={refetchFundListAnalytics}
        />,
        <PCompanySectorPieChart widgetId={`companies-per-sector-widget`} />,
        <PCompanyInvestedPieChart widgetId={`invested-per-sector-widget`} />,
        <AggrGrossIrrLineChart widgetId={`aggr-gross-irr-widget`} />,
        <AggrGrossMoicLineChart widgetId={`aggr-gross-moic-widget`} />,
        <EMSBlotterWidget widgetId={`ems-blotter-widget`} />,
      ]);
    }
  }, [refetchFundListAnalytics]);

  const handleSaveLayout = useCallback((config: Config[]) => {
    // react-grid-layout returns objects that use "i" as the identifier.
    // Convert that to our expected "id" field before saving.
    const transformedConfig = (config as Array<Record<string, unknown>>).map(
      ({ i, id: _discard, ...rest }) => ({
        id: i as string,
        ...rest,
      }),
    );
    localStorage.setItem('layout', JSON.stringify(transformedConfig));
  }, []);

  const handleAddWidget = useCallback(
    (widget: WidgetGenerationRequest) => {
      switch (widget.id) {
        case 'companies-per-sector':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <PCompanySectorPieChart
              widgetId={`companies-per-sector-widget-${crypto.randomUUID()}`}
            />,
          ]);
          break;
        case 'invested-per-sector':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <PCompanyInvestedPieChart
              widgetId={`invested-per-sector-widget-${crypto.randomUUID()}`}
            />,
          ]);
          break;

        case 'aggr-gross-moic':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <AggrGrossMoicLineChart
              widgetId={`aggr-gross-moic-widget-${crypto.randomUUID()}`}
            />,
          ]);
          break;

        case 'aggr-gross-irr':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <AggrGrossIrrLineChart
              widgetId={`aggr-gross-irr-widget-${crypto.randomUUID()}`}
            />,
          ]);
          break;
        case 'fund-list':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <FundListDashboard
              widgetId={`fund-list-widget-${crypto.randomUUID()}`}
              refetchFundListAnalytics={refetchFundListAnalytics}
            />,
          ]);
          break;
        case 'ems-blotter':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <EMSBlotterWidget
              widgetId={`ems-blotter-widget-${crypto.randomUUID()}`}
            />,
          ]);
          break;
        case 'historical-returns':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <HistoricalReturnsWidgetWrapper
              widgetId={`historical-returns-widget-${crypto.randomUUID()}`}
              title="Historical Returns"
            />,
          ]);
          break;
        case 'forward-returns':
          setWidgets((prevWidgets) => [
            ...prevWidgets,
            <ForwardReturnsWidgetWrapper
              widgetId={`forward-returns-widget-${crypto.randomUUID()}`}
              title="Forward Returns"
            />,
          ]);
          break;
        default:
          break;
      }
    },
    [refetchFundListAnalytics],
  );

  useEffect(() => {
    if (
      location.state &&
      (location.state as { widgetId?: WidgetId }).widgetId
    ) {
      const widgetId = (location.state as { widgetId?: WidgetId }).widgetId;
      if (widgetId) {
        console.log('Adding widget from dropdown:', widgetId);
        handleAddWidget({ id: widgetId });

        // Clear the state by navigating to the same location without state
        navigate({
          to: location.pathname,
          search: location.search,
          replace: true,
          state: {},
        });
      }
    }
  }, [handleAddWidget, location, navigate]);

  const handleRemoveWidget = useCallback((widgetId: string) => {
    setWidgets((prevWidgets) =>
      prevWidgets.filter((widget) => widget.props.widgetId !== widgetId),
    );
  }, []);

  const getAttr = (idx: number, attr: string) => {
    const layout = JSON.parse(
      localStorage.getItem('layout') || '[]',
    ) as Config[];
    switch (attr) {
      case 'w':
        return layout[idx]?.[attr] || 6;
      case 'h':
        return layout[idx]?.[attr] || 9;
      case 'x':
        return layout[idx]?.[attr] || 0;
      case 'y':
        return layout[idx]?.[attr] || 0;
      default:
        return 6;
    }
  };

  const toggleMetricsVisibility = () => {
    setShowMetrics((prevState) => !prevState);
  };

  const handleAddAllWidgets = useCallback(() => {
    const allWidgetTypes = STATIC_WIDGET_IDS;

    allWidgetTypes.forEach((type) => {
      handleAddWidget({ id: type });
    });
  }, [handleAddWidget]);

  const handleRemoveAllWidgets = useCallback(() => {
    setWidgets([]);
  }, []);

  useEffect(() => {
    if (!widgetOperation) {
      return; // Early return if no operation
    }
    switch (widgetOperation.action) {
      case 'add':
        if (!widgetOperation.widgets) {
          break; // Skip if no widgetIds
        }
        if (widgetOperation.widgets.length === 0) {
          // Empty array means add all widgets
          handleAddAllWidgets();
        } else {
          // Add specific widgets from array
          widgetOperation.widgets.forEach((widget) => {
            handleAddWidget(widget);
          });
        }
        break;

      case 'remove':
        if (!widgetOperation.widgets) {
          break; // Skip if no widgetIds
        }
        if (widgetOperation.widgets.length === 0) {
          // Empty array means remove all widgets
          handleRemoveAllWidgets();
        } else {
          // Remove specific widgets from array
          widgetOperation.widgets.forEach((widget) => {
            const widgetsToRemove = widgets.filter((w) =>
              w.props.widgetId.includes(widget.id),
            );
            widgetsToRemove.forEach((w) => {
              handleRemoveWidget(w.props.widgetId);
            });
          });
        }
        break;
    }
    // Clear the operation after handling
    setWidgetOperation(null);
  }, [
    widgetOperation,
    handleAddWidget,
    handleRemoveWidget,
    handleRemoveAllWidgets,
    handleAddAllWidgets,
    widgets,
    setWidgetOperation,
  ]);

  return (
    <>
      <HeaderContainer>
        <Breadcrumb
          items={[
            { name: 'Fund Overview', url: '/fund/overview' },
            ...(selectedFund?.name ? [{ name: selectedFund?.name }] : []),
          ]}
        />
        <PageTagline message="Dashboard" />
      </HeaderContainer>
      <PageContent>
        <div
          className={`flex align-items-center ${showMetrics ? 'pt-4' : 'pt-2 pb-1'}`}
        >
          <Button
            icon={showMetrics ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
            className="p-button-rounded p-button-text mr-2"
            onClick={toggleMetricsVisibility}
            tooltip={showMetrics ? 'Collapse metrics' : 'Expand metrics'}
            tooltipOptions={{ position: 'right' }}
          />
          <span className="text-xs font-medium">Overview Metrics</span>
        </div>

        {showMetrics && (
          <div className="grid text-sm pt-2">
            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Total AUM</p>
              <h4 className="m-0 font-normal">
                {totalAum ? numberStd(totalAum) : '-'}
              </h4>
            </div>

            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Total Assets Deployed</p>
              <h4 className="m-0 font-normal">
                {totalAssetsDeployed ? numberStd(totalAssetsDeployed) : '-'}
              </h4>
            </div>

            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Portfolio Companies</p>
              <h4 className="m-0 font-normal">{totalPCompanies}</h4>
            </div>

            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Exit</p>
              <h4 className="m-0 font-normal">{exited}</h4>
            </div>

            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Active Funds</p>
              <h4 className="m-0 font-normal">{activeFunds}</h4>
            </div>

            <div className="col text-center flex-column justify-content-center	align-content-center">
              <p className="mb-2 mt-0 font-medium">Write-off</p>
              <h4 className="m-0 font-normal">{writeOff}</h4>
            </div>
          </div>
        )}

        <Divider className={showMetrics ? '' : 'mt-0'} />
        <GridLayout
          className="layout"
          cols={12}
          rowHeight={24}
          margin={[28, 28]}
          draggableHandle=".drag-handler"
          compactType="vertical"
          onLayoutChange={handleSaveLayout}
        >
          {widgets.map((widget, index) => (
            <div
              key={widget.props.widgetId}
              className="grid-stack-item-content overflow-auto"
              data-grid={{
                x: getAttr(index, 'x'),
                y: getAttr(index, 'y'),
                w: getAttr(index, 'w'),
                h: getAttr(index, 'h'),
              }}
            >
              {React.cloneElement(widget, {
                handleRemoveWidget,
              })}
            </div>
          ))}
        </GridLayout>
      </PageContent>
    </>
  );
};
